import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getEvents } from "core-integration/src/store/events/reducers";
import { updateClassicLeague } from "core-integration/src/store/leagues/thunks";
import {
  IPrivateLeagueAdmin,
  IUpdateClassicLeagueData,
  LeagueCodePrivacy,
} from "core-integration/src/store/leagues/types";
import React, { useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Box } from "rebass/styled-components";
import Button from "../../Button";
import {
  CheckboxField,
  FieldWrap,
  InputField,
  SelectField,
} from "../../FieldRenderers";
import SubHeading from "../../SubHeading";

interface IOwnProps {
  league: IPrivateLeagueAdmin;
}

type Props = IOwnProps & WithTranslation;

interface IState {
  codePrivacy: LeagueCodePrivacy;
  closed: boolean;
  name: string;
  startEvent: number;
}

const ClassicDetails: React.FC<Props> = ({ league, t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();

  const [state, setState] = useState<IState>({
    closed: league.closed,
    codePrivacy: league.code_privacy,
    name: league.name,
    startEvent: league.start_event,
  });

  const updateLeague = (leagueId: number, data: IUpdateClassicLeagueData) =>
    dispatch(updateClassicLeague(leagueId, data));

  const events = useSelector((state: RootState) => getEvents(state));

  const requiredFields: Array<keyof IState> = [
    "codePrivacy",
    "name",
    "startEvent",
  ];

  const privacyOptions: { [key: string]: string } = {
    a: "All game players",
    l: "League members",
    p: "League administrator",
  };

  const apiDataFromState = (): IUpdateClassicLeagueData => ({
    admin_entry: league.admin_entry,
    closed: state.closed,
    code_privacy: state.codePrivacy,
    name: state.name,
    start_event: state.startEvent,
  });

  const handleNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, name: e.currentTarget.value });

  const handleCodePrivacyChange = (
    e: React.SyntheticEvent<HTMLSelectElement>,
  ) =>
    setState({
      ...state,
      codePrivacy: e.currentTarget.value as LeagueCodePrivacy,
    });

  const handleStartEventChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, startEvent: parseInt(e.currentTarget.value, 10) });

  const handleClosedChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, closed: e.currentTarget.checked ? true : false });

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    await updateLeague(league.id, apiDataFromState());
    navigate("/leagues");
  };

  const disabled = requiredFields.some((e) => !state[e]);
  return (
    <>
      <SubHeading>{t("classicDetails.title", "League details")}</SubHeading>
      <form onSubmit={handleSubmit}>
        <FieldWrap>
          <InputField
            id="ismLeagueName"
            label={t("classicDetails.labels.name", "League name")}
            value={state.name}
            onChange={handleNameChange}
            hint={t("classicDetails.hints.name", "Maximum 30 characters")}
            maxLength={30}
            disabled={league.name_change_blocked}
          />
        </FieldWrap>
        <Box mb={4}>
          <p>
            {league.name_change_blocked
              ? t(
                  "classicDetails.p1n",
                  "Your requested league name is currently pending moderation and can't be edited at this time.",
                )
              : t(
                  "classicDetails.p1",
                  "Please think carefully before entering your league name. Leagues with names that are deemed inappropriate or offensive may result in your account being deleted. Please refer to the Terms & Conditions of entry for more information.",
                )}
          </p>
        </Box>
        <FieldWrap>
          <SelectField
            id="ismLeagueCodePrivacy"
            value={state.codePrivacy}
            onChange={handleCodePrivacyChange}
            label={t("classicDetails.labels.privacy", "League code privacy")}
            hint={t(
              "classicDetails.hints.privacy",
              "This sets who the league code is viewable by",
            )}
          >
            {Object.keys(privacyOptions).map((p) => (
              <option value={p} aria-selected={p === state.codePrivacy} key={p}>
                {privacyOptions[p]}
              </option>
            ))}
          </SelectField>
        </FieldWrap>
        <FieldWrap>
          <SelectField
            id="ismLeagueStartEvent"
            value={state.startEvent}
            onChange={handleStartEventChange}
            label={t("classicDetails.labels.scoringStarts", "Scoring starts")}
            hint={t(
              "classicDetails.hints.scoringStarts",
              "Any change will be reflected after the next points update",
            )}
          >
            {events.map((e) => (
              <option
                value={`${e.id}`}
                aria-selected={e.id === state.startEvent}
                key={e.id}
              >
                {e.name}
              </option>
            ))}
          </SelectField>
        </FieldWrap>
        <FieldWrap>
          <CheckboxField
            checked={state.closed}
            id="ismLeagueClosed"
            label={t(
              "classicDetails.labels.closeLeague",
              "Close league to new entries",
            )}
            onChange={handleClosedChange}
          />
        </FieldWrap>
        <FieldWrap>
          <Button type="submit" disabled={disabled}>
            {t("classicDetails.cta", "Update league")}
          </Button>
        </FieldWrap>
      </form>
    </>
  );
};

export default withTranslation()(ClassicDetails);
